import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  Buildings, ClipboardText, ShieldCheck, FileText, Plus, ArrowRight,
  Warning, CheckCircle, Clock, HardHat, Binoculars, FolderOpen,
} from '@phosphor-icons/react';

function StatCard({ value, label, icon: Icon, iconColor, bgColor, testId, linkTo }) {
  const content = (
    <Card className={`chantix-card ${linkTo ? 'h-full border-dashed border-red-200 hover:border-red-400 transition-colors cursor-pointer' : ''}`} data-testid={testId}>
      <CardContent className={`p-4 ${linkTo ? 'h-full flex items-center' : ''}`}>
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-lg ${bgColor}`}>
            <Icon size={24} className={iconColor} weight="fill" />
          </div>
          <div>
            <p className="text-2xl font-bold text-[#111827]">{value}</p>
            <p className="text-sm text-[#4B5563]">
              {label}
              {linkTo && <ArrowRight size={12} className="inline ml-1" />}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
  return linkTo ? <Link to={linkTo} data-testid={`${testId}-link`}>{content}</Link> : content;
}

const OPC_STATS = [
  { key: 'chantiers_active', getValue: s => s?.chantiers?.active || 0, label: 'Chantiers actifs', icon: Buildings, iconColor: 'text-[#0055FF]', bgColor: 'bg-blue-100', testId: 'stat-chantiers' },
  { key: 'réserves_open', getValue: s => s?.réserves?.open || 0, label: 'Réserves ouvertes', icon: ClipboardText, iconColor: 'text-amber-600', bgColor: 'bg-amber-100', testId: 'stat-réserves' },
  { key: 'reports_total', getValue: s => s?.reports?.total || 0, label: 'Rapports générés', icon: FileText, iconColor: 'text-emerald-600', bgColor: 'bg-emerald-100', testId: 'stat-reports' },
  { key: 'reserves_resolved', getValue: s => s?.réserves?.resolved || 0, label: 'Réserves levées', icon: CheckCircle, iconColor: 'text-emerald-600', bgColor: 'bg-emerald-50', testId: 'stat-reserves-resolved' },
];

const CSPS_STATS = [
  { key: 'chantiers_csps', getValue: s => s?.chantiers?.csps || 0, label: 'Chantiers CSPS', icon: Buildings, iconColor: 'text-[#FF6B00]', bgColor: 'bg-orange-100', testId: 'stat-chantiers' },
  { key: 'security_obs', getValue: s => s?.observations?.security || 0, label: 'Obs. sécurité', icon: ShieldCheck, iconColor: 'text-red-600', bgColor: 'bg-red-100', testId: 'stat-security' },
  { key: 'inspections', getValue: s => s?.csps?.inspections || 0, label: 'Inspections', icon: HardHat, iconColor: 'text-amber-600', bgColor: 'bg-amber-100', testId: 'stat-inspections' },
  { key: 'visites', getValue: s => s?.csps?.visites || 0, label: 'Visites chantier', icon: Binoculars, iconColor: 'text-emerald-600', bgColor: 'bg-emerald-100', testId: 'stat-visites' },
];

export function DashboardStatsGrid({ stats, isCSPS }) {
  const items = isCSPS ? CSPS_STATS : OPC_STATS;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="stats-grid">
      {items.map(item => (
        <StatCard key={item.key} value={item.getValue(stats)} label={item.label}
          icon={item.icon} iconColor={item.iconColor} bgColor={item.bgColor}
          testId={item.testId} linkTo={item.linkTo} />
      ))}
    </div>
  );
}

function SeverityIcon({ severity }) {
  if (severity === 'high') return <Warning size={20} className="text-red-500 flex-shrink-0 mt-0.5" weight="fill" />;
  if (severity === 'medium') return <Clock size={20} className="text-amber-500 flex-shrink-0 mt-0.5" weight="fill" />;
  return <CheckCircle size={20} className="text-emerald-500 flex-shrink-0 mt-0.5" weight="fill" />;
}

function getPriorityBorderColor(priority) {
  if (priority === 'high') return 'border-l-red-500';
  if (priority === 'medium') return 'border-l-amber-500';
  return 'border-l-emerald-500';
}

export function DashboardRecentActivity({ stats, isCSPS, getStatusBadge, getPriorityBadge }) {
  return (
    <div className={`grid ${isCSPS ? 'md:grid-cols-2' : 'grid-cols-1'} gap-6`}>
      {isCSPS && (
        <Card className="chantix-card" data-testid="recent-observations">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-bold">Observations sécurité</CardTitle>
              <Link to="/sécurité" className="text-[#FF6B00] text-sm font-medium flex items-center gap-1 hover:underline">
                Voir tout <ArrowRight size={16} />
              </Link>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {stats?.recent_observations?.length > 0 ? (
              stats.recent_observations.map(obs => (
                <div key={obs.id} className="p-3 rounded-md bg-gray-50">
                  <div className="flex items-start gap-3">
                    <SeverityIcon severity={obs.severity} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#111827] truncate">{obs.title}</p>
                      <p className="text-sm text-[#4B5563] line-clamp-2">{obs.description}</p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-6 text-[#4B5563]">
                <ShieldCheck size={40} className="mx-auto mb-2 opacity-50" />
                <p>Aucune observation sécurité</p>
                <Link to="/sécurité/nouvelle"><Button variant="link" className="text-[#FF6B00]">Ajouter une observation</Button></Link>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {isCSPS && (
        <Card className="chantix-card" data-testid="recent-inspections">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-bold">Dernières inspections</CardTitle>
              <Link to="/csps/documents" className="text-[#FF6B00] text-sm font-medium flex items-center gap-1 hover:underline">
                Documents <ArrowRight size={16} />
              </Link>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {stats?.recent_inspections?.length > 0 ? (
              stats.recent_inspections.map(insp => (
                <div key={insp.id} className="p-3 rounded-md bg-orange-50/50 border-l-4 border-l-[#FF6B00]">
                  <p className="font-medium text-[#111827] truncate">{insp.entreprise}</p>
                  <p className="text-sm text-[#4B5563] truncate">{insp.chantier_name}</p>
                  {insp.created_at && <p className="text-xs text-[#9CA3AF] mt-0.5">{new Date(insp.created_at).toLocaleDateString('fr-FR')}</p>}
                </div>
              ))
            ) : (
              <div className="text-center py-6 text-[#4B5563]">
                <HardHat size={40} className="mx-auto mb-2 opacity-50" />
                <p>Aucune inspection realisee</p>
                <Link to="/csps/documents"><Button variant="link" className="text-[#FF6B00]">Générer une inspection</Button></Link>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {!isCSPS && (
        <Card className="chantix-card" data-testid="recent-réserves">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-bold">Réserves recentes</CardTitle>
              <Link to="/réserves" className="text-[#0055FF] text-sm font-medium flex items-center gap-1 hover:underline">
                Voir tout <ArrowRight size={16} />
              </Link>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {stats?.recent_réserves?.length > 0 ? (
              stats.recent_réserves.map(réserve => (
                <div key={réserve.id} className={`p-3 rounded-md bg-gray-50 border-l-4 ${getPriorityBorderColor(réserve.priority)}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#111827] truncate">{réserve.title}</p>
                      <p className="text-sm text-[#4B5563] truncate">{réserve.location}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {getStatusBadge(réserve.status)}
                      {getPriorityBadge(réserve.priority)}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-6 text-[#4B5563]">
                <ClipboardText size={40} className="mx-auto mb-2 opacity-50" />
                <p>Aucune réserve pour le moment</p>
                <Link to="/réserves/nouvelle"><Button variant="link" className="text-[#0055FF]">Créer une réserve</Button></Link>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

const CSPS_ACTIONS = [
  { to: '/sécurité/nouvelle', icon: ShieldCheck, color: 'text-red-500', border: 'border-2 border-red-200 hover:border-red-400', label: 'Obs. sécurité', testId: 'quick-security' },
  { to: '/csps/documents', icon: FolderOpen, color: 'text-[#FF6B00]', border: 'border-2 border-orange-200 hover:border-orange-400', label: 'Documents CSPS', testId: 'quick-csps-docs' },
  { to: '/csps/visites', icon: Binoculars, color: 'text-emerald-600', border: 'border-2', label: 'Visites', testId: 'quick-visites' },
  { to: '/csps/chantiers', icon: Buildings, color: 'text-[#111827]', border: 'border-2', label: 'Chantiers CSPS', testId: 'quick-chantier-csps' },
];

const OPC_ACTIONS = [
  { to: '/réserves/nouvelle', icon: ClipboardText, color: 'text-[#0055FF]', border: 'border-2', label: 'Nouvelle réserve', testId: 'quick-réserve' },
  { to: '/documents-opc', icon: FileText, color: 'text-emerald-600', border: 'border-2', label: 'Générer rapport', testId: 'quick-report' },
  { to: '/chantiers/nouveau', icon: Buildings, color: 'text-[#111827]', border: 'border-2', label: 'Nouveau chantier', testId: 'quick-chantier' },
  { to: '/planning', icon: Clock, color: 'text-amber-500', border: 'border-2', label: 'Planning', testId: 'quick-planning' },
];

export function DashboardQuickActions({ isCSPS }) {
  const actions = isCSPS ? CSPS_ACTIONS : OPC_ACTIONS;
  return (
    <Card className="chantix-card" data-testid="quick-actions">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-bold">Actions rapides</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {actions.map(a => (
            <Link key={a.testId} to={a.to}>
              <Button variant="outline" className={`w-full h-auto py-4 flex flex-col gap-2 ${a.border}`} data-testid={a.testId}>
                <a.icon size={24} className={a.color} />
                <span className="text-sm">{a.label}</span>
              </Button>
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
